import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    TextField,
    InputAdornment,
    Link,
    useMediaQuery,
    useTheme,
    IconButton,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import CloseIcon from "@mui/icons-material/Close";
import SMButtonBrown from "../navigation/SMButtonBrown";
import { CervinoHeaderText } from "../typography/CervinoHeaderText";
import topCornersImg from "../../assets/icons/SM_short_flower_black.png";
import bottomCornersImg from "../../assets/icons/SM_long_flower_black.png";

const GOOGLE_FORM_ACTION_URL =
    "https://docs.google.com/forms/d/e/1FAIpQLSfdv0p-7yOwsMO37wuUhXte_ujqFITGlSWOpo0OF8gWPhRJ_g/formResponse";
const GOOGLE_EMAIL_ENTRY_KEY = "entry.60497188";

const submitToGoogleForm = async (email) => {
    const formData = new FormData();
    formData.append(GOOGLE_EMAIL_ENTRY_KEY, email);

    await fetch(GOOGLE_FORM_ACTION_URL, {
        method: "POST",
        mode: "no-cors",
        body: formData,
    });
};

function CardCornerImage({ src, position, sx, mirrorX = false }) {
    return (
        <Box
            component="img"
            src={src}
            alt=""
            aria-hidden
            sx={{
                position: "absolute",
                pointerEvents: "none",
                userSelect: "none",
                transform: mirrorX ? "scaleX(-1)" : "none",
                ...position,
                ...sx,
            }}
        />
    );
}

export default function NewsLetter({
                                       heroImage,
                                       logoSrc,
                                       onSubmit,
                                       onSkip,
                                       onClose,

                                       title = "JOIN THE TESTER WAITLIST!",
                                       placeholder = "Your email address...",
                                       ctaLabel = "JOIN NOW",
                                       skipLabel = "I'll join next time",
                                       initialEmail = "",
                                       disabled = false,

                                       panelBg = "#7A3E23",
                                       cardBg = "#F9F0E0",
                                       cardBorderRadius = 5,
                                   }) {
    const theme = useTheme();
    const [email, setEmail] = useState(initialEmail);
    const [error, setError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);

    // xs/sm/md -> true, lg/xl -> false
    const isSmallToMd = useMediaQuery((theme) => theme.breakpoints.down("lg"));

    // 🔑 ESC key handling
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "Escape") onClose?.();
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const isValidEmail = useMemo(() => {
        if (!email) return false;
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    }, [email]);

    const handleSubmit = async () => {
        if (!isValidEmail) {
            setError("Please enter a valid email address.");
            return;
        }
        if (isSubmitting) return;

        setError("");
        const cleanEmail = email.trim();

        try {
            setIsSubmitting(true);
            await submitToGoogleForm(cleanEmail);

            // Important: notify parent AFTER submit succeeds
            onSubmit?.(cleanEmail);
        } catch {
            setError("Something went wrong. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const CloseButton = (
        <IconButton
            aria-label="Close newsletter"
            onClick={onClose}
            sx={{
                position: "fixed",
                top: { xs: 20, sm: 20, md: 10, lg: 5, xl: 5 },
                right: { xs: 20, sm: 20, md: 10, lg: 5, xl: 5 },
                zIndex: 5,
                color: isSmallToMd ? "#fff" : theme.palette.primary.main,
                backgroundColor: isSmallToMd
                    ? "rgba(0,0,0,0.4)"
                    : "rgba(255,255,255,0.6)",
                backdropFilter: "blur(4px)",
                "&:hover": {
                    backgroundColor: isSmallToMd
                        ? "rgba(0,0,0,0.6)"
                        : "rgba(255,255,255,0.85)",
                },
            }}
        >
            <CloseIcon />
        </IconButton>
    );

    // Shared card content so we don't duplicate markup/styling
    const CardContent = (
        <Card
            sx={{
                position: "relative",
                overflow: "hidden",
                width: isSmallToMd ? "100%" : "90%",
                height: isSmallToMd ? "auto" : "90%",
                maxWidth: isSmallToMd ? { xs: 360, sm: 420, md: 480 } : "none",
                p: { xs: 3, sm: 4, md: 5, lg: 5 },
                textAlign: "center",
                backgroundColor: cardBg,
                borderRadius: cardBorderRadius,
            }}
        >
            {/* ✅ Decorative layer (always behind) */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    zIndex: 0,
                    pointerEvents: "none",
                }}
            >
                <CardCornerImage
                    src={topCornersImg}
                    position={{ top: 0, left: 0 }}
                    sx={{
                        width: { xs: 100, sm: 110, md: 120, lg: 140, xl: 200 },
                        transform: {
                            xs: "translate(-50px, -50px) rotate(135deg)",
                            sm: "translate(-55px, -55px) rotate(135deg)",
                            md: "translate(-60px, -60px) rotate(135deg)",
                            lg: "translate(-70px, -70px) rotate(135deg)",
                            xl: "translate(-80px, -80px) rotate(135deg)",
                        },
                    }}
                />

                <CardCornerImage
                    src={topCornersImg}
                    mirrorX
                    position={{ top: 0, right: 0 }}
                    sx={{
                        width: { xs: 100, sm: 110, md: 120, lg: 140, xl: 200 },
                        transform: {
                            xs: "translate(50px, -50px) rotate(-135deg)",
                            sm: "translate(55px, -55px) rotate(-135deg)",
                            md: "translate(60px, -60px) rotate(-135deg)",
                            lg: "translate(70px, -70px) rotate(-135deg)",
                            xl: "translate(80px, -80px) rotate(-135deg)",
                        },
                    }}
                />

                <CardCornerImage
                    src={bottomCornersImg}
                    position={{ bottom: 0, left: 0 }}
                    sx={{
                        width: { xs: 120, sm: 140, md: 160, lg: 240, xl: 400 },
                        transform: {
                            xs: "translate(-70px, 10px) rotate(0deg)",
                            sm: "translate(-80px, 10px) rotate(0deg)",
                            md: "translate(-85px, 20px) rotate(0deg)",
                            lg: "translate(-110px, 80px) rotate(0deg)",
                            xl: "translate(-180px, 140px) rotate(0deg)",
                        },
                    }}
                />

                <CardCornerImage
                    src={bottomCornersImg}
                    mirrorX
                    position={{ bottom: 0, right: 0 }}
                    sx={{
                        width: { xs: 120, sm: 140, md: 160, lg: 240, xl: 400 },
                        transform: {
                            xs: "translate(66px, 10px) rotate(0deg)",
                            sm: "translate(76px, 10px) rotate(0deg)",
                            md: "translate(83px, 20px) rotate(0deg)",
                            lg: "translate(110px, 80px) rotate(0deg)",
                            xl: "translate(180px, 140px) rotate(0deg)",
                        },
                    }}
                />
            </Box>

            {/* ✅ Content layer (always above) */}
            <Box sx={{ position: "relative", zIndex: 1 }}>
                {logoSrc && (
                    <Box
                        component="img"
                        src={logoSrc}
                        alt="Logo"
                        sx={{
                            width: { xs: 220, sm: 260, md: 300, lg: 350, xl: 350 },
                            mb: { xs: 1, sm: 3 },
                        }}
                    />
                )}

                <CervinoHeaderText
                    sx={{
                        fontSize: { xs: "1rem", sm: "1.5rem", md: "1.5rem", lg: "2rem", xl: "2rem" },
                        mb: { xs: 2, sm: 3 },
                    }}
                >
                    {title}
                </CervinoHeaderText>

                <TextField
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={placeholder}
                    error={!!error}
                    helperText={error || " "}
                    disabled={disabled || isSubmitting}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MailOutlineIcon className="newsletter-mail-icon" />
                                </InputAdornment>
                            ),
                        },
                    }}
                    sx={{
                        mb: 1,
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: "#fff",
                            fontFamily: "'League Spartan', Arial, sans-serif",
                        },
                        "& .MuiOutlinedInput-input": {
                            fontFamily: "'League Spartan', Arial, sans-serif",
                            fontSize: "1rem",
                        },
                        "& .MuiOutlinedInput-input::placeholder": {
                            fontFamily: "'League Spartan', Arial, sans-serif",
                            opacity: 0.5,
                        },
                        "& .MuiFormHelperText-root": {
                            fontFamily: "'League Spartan', Arial, sans-serif",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.palette.primary.main,
                        },
                        "& .newsletter-mail-icon": {
                            color: theme.palette.grey[500],
                            transition: "color 150ms ease",
                        },
                        "& .MuiOutlinedInput-root.Mui-focused .newsletter-mail-icon": {
                            color: theme.palette.primary.main,
                        },
                    }}
                />

                <SMButtonBrown
                    fullWidth
                    onClick={handleSubmit}
                    disabled={disabled || isSubmitting}
                    sx={{ marginBottom: { xs: 1, sm: 2 } }}
                >
                    {isSubmitting ? "Joining..." : ctaLabel}
                </SMButtonBrown>

                <Link
                    component="button"
                    underline="always"
                    onClick={onSkip}
                    sx={{
                        color: "text.secondary",
                        fontSize: { xs: 14, sm: 14, md: 18, lg: 20, xl: 20 },
                        opacity: isSubmitting ? 0.6 : 1,
                        pointerEvents: isSubmitting ? "none" : "auto",
                    }}
                >
                    {skipLabel}
                </Link>
            </Box>
        </Card>
    );

    if (isSmallToMd) {
        return (
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundImage: `url(${heroImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    position: "relative",
                }}
            >
                {CloseButton}

                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backgroundColor: "rgba(0,0,0,0.35)",
                        zIndex: 1,
                    }}
                />

                <Box
                    sx={{
                        position: "relative",
                        zIndex: 2,
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        px: { xs: 2, sm: 3 },
                    }}
                >
                    {CardContent}
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "row",
                backgroundColor: panelBg,
            }}
        >
            {CloseButton}

            <Box
                sx={{
                    flex: 6,
                    backgroundImage: `url(${heroImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            />

            <Box
                sx={{
                    flex: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                {CardContent}
            </Box>
        </Box>
    );
}
